import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { type OrpcClient } from '@/orpc/client'
import {
  ACCOUNTING_GROUP_BY_VALUES,
  ACCOUNTING_REPORT_VALUES,
  type AccountingGroupBy,
  type AccountingReport,
} from '@/model/middleware-types'
import {
  BILLING_DISPLAY_TIMEZONE,
  BILLING_ENTITY_OPTIONS,
  type BillingEntityValue,
} from '@/pages/Billing/utils'
import { getDateRangeError, VISIT_STATUS_OPTIONS } from '../state'

dayjs.extend(utc)
dayjs.extend(timezone)

export const ACCOUNTING_FILTER_STORAGE_KEY = 'billing-accounting-filter-data'

export const ACCOUNTING_VISIT_STATUS_OPTIONS = [
  ...VISIT_STATUS_OPTIONS,
  { value: 'cancelled', label: 'Cancelled' },
] as const

export type AccountingVisitStatusValue = (typeof ACCOUNTING_VISIT_STATUS_OPTIONS)[number]['value']

export type AccountingFilters = {
  /** Date-only string (YYYY-MM-DD) in the billing display timezone. */
  dateFrom: string
  /** Date-only string (YYYY-MM-DD) in the billing display timezone. */
  dateTo: string
  billingEntities: BillingEntityValue[]
  patientStates: string[]
  practitionerIds: string[]
  payerNames: string[]
  visitStatuses: AccountingVisitStatusValue[]
}

export type AccountingView = { report: AccountingReport; groupBy: AccountingGroupBy }

export type AccountingState = { filters: AccountingFilters; view: AccountingView }

export type ReportInput = Parameters<OrpcClient['billingV2Report']>[0]

export const REPORT_LABELS: Record<AccountingReport, string> = {
  revenue: 'Revenue',
  payments: 'Payments',
  outstanding: 'Outstanding balances',
}

export const SUMMARY_TITLES: Record<AccountingReport, string> = {
  revenue: 'Revenue',
  payments: 'Payments',
  outstanding: 'A/R & aging',
}

export const GROUP_BY_LABELS: Record<AccountingGroupBy, string> = {
  entity: 'PC / legal entity',
  state: 'Encounter state',
  provider: 'Provider',
  payer: 'Payer',
  method: 'Payment method',
}

export const INVALID_RANGE_MESSAGE = 'Select a valid date range.'

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

export function getDefaultAccountingFilters(): AccountingFilters {
  const today = dayjs().tz(BILLING_DISPLAY_TIMEZONE)

  return {
    dateFrom: today.startOf('month').format('YYYY-MM-DD'),
    dateTo: today.format('YYYY-MM-DD'),
    billingEntities: [],
    patientStates: [],
    practitionerIds: [],
    payerNames: [],
    visitStatuses: [],
  }
}

export function getDefaultAccountingView(): AccountingView {
  return { report: 'revenue', groupBy: 'entity' }
}

function sanitizeValues<T extends string>(raw: unknown, allowed: readonly T[]): T[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((value): value is T => (allowed as readonly unknown[]).includes(value))
}

function sanitizeStrings(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((value): value is string => typeof value === 'string')
}

function sanitizeDate(raw: unknown, fallback: string): string {
  return typeof raw === 'string' && DATE_ONLY_REGEX.test(raw) ? raw : fallback
}

function sanitizeOne<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return (allowed as readonly unknown[]).includes(raw) ? (raw as T) : fallback
}

export function readPersistedAccountingState(): AccountingState {
  const defaultFilters = getDefaultAccountingFilters()
  const defaultView = getDefaultAccountingView()

  try {
    const raw = localStorage.getItem(ACCOUNTING_FILTER_STORAGE_KEY)
    if (!raw) return { filters: defaultFilters, view: defaultView }

    const parsed = JSON.parse(raw) as {
      filters?: Partial<Record<keyof AccountingFilters, unknown>>
      view?: Partial<Record<keyof AccountingView, unknown>>
    }
    const filters = parsed.filters ?? {}
    const view = parsed.view ?? {}

    return {
      filters: {
        dateFrom: sanitizeDate(filters.dateFrom, defaultFilters.dateFrom),
        dateTo: sanitizeDate(filters.dateTo, defaultFilters.dateTo),
        billingEntities: sanitizeValues(
          filters.billingEntities,
          BILLING_ENTITY_OPTIONS.map((option) => option.value),
        ),
        patientStates: sanitizeStrings(filters.patientStates),
        practitionerIds: sanitizeStrings(filters.practitionerIds),
        payerNames: sanitizeStrings(filters.payerNames),
        visitStatuses: sanitizeValues(
          filters.visitStatuses,
          ACCOUNTING_VISIT_STATUS_OPTIONS.map((option) => option.value),
        ),
      },
      view: {
        report: sanitizeOne(view.report, ACCOUNTING_REPORT_VALUES, defaultView.report),
        groupBy: sanitizeOne(view.groupBy, ACCOUNTING_GROUP_BY_VALUES, defaultView.groupBy),
      },
    }
  } catch {
    return { filters: defaultFilters, view: defaultView }
  }
}

export function persistAccountingState(state: AccountingState): void {
  try {
    localStorage.setItem(ACCOUNTING_FILTER_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore quota/serialization errors
  }
}

export function getAccountingRangeError(
  filters: Pick<AccountingFilters, 'dateFrom' | 'dateTo'>,
): string | null {
  const datesValid = [filters.dateFrom, filters.dateTo].every(
    (date) =>
      DATE_ONLY_REGEX.test(date) &&
      dayjs.tz(`${date} 00:00:00`, BILLING_DISPLAY_TIMEZONE).isValid(),
  )
  if (!datesValid || filters.dateTo < filters.dateFrom) return INVALID_RANGE_MESSAGE

  return getDateRangeError(filters)
}

export function effectiveGroupBy(view: AccountingView): AccountingGroupBy {
  return view.groupBy === 'method' && view.report !== 'payments' ? 'entity' : view.groupBy
}

export function buildReportInput(filters: AccountingFilters, view: AccountingView): ReportInput {
  return {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    report: view.report,
    groupBy: effectiveGroupBy(view),
    ...(filters.billingEntities.length > 0 && { billingEntity: filters.billingEntities }),
    ...(filters.patientStates.length > 0 && { patientState: filters.patientStates }),
    ...(filters.practitionerIds.length > 0 && { practitionerId: filters.practitionerIds }),
    ...(filters.payerNames.length > 0 && { payerName: filters.payerNames }),
    ...(filters.visitStatuses.length > 0 && { appointmentStatus: filters.visitStatuses }),
  }
}
